import {
  Building2, Globe2, Map, MapPin, Layers, Store, Users, type LucideIcon,
} from 'lucide-react';

/** 組織階層ノードのアイコン+背景chip (`docs/design/ui-guidelines.md` §2.2 準拠)。
 *  HierarchyPage.tsx / NodesPage.tsx の両方でノード種別ごとの視覚差別化に使う (Design CRITICAL 対応、
 *  2026-08-03 レビュー: 全階層が同一の Building2 アイコン+navy色で描画され差別化されていなかった)。
 *
 *  node_type_definitions.icon (DB保存済みのlucideアイコン名文字列) → コンポーネントのマッピング。
 *  未知の値・null は Building2 にフォールバックする。 */
const ICON_MAP: Record<string, LucideIcon> = {
  Building2, Globe2, Map, MapPin, Layers, Store, Users,
};

export const resolveNodeTypeIcon = (icon: string | null): LucideIcon => (icon ? ICON_MAP[icon] ?? Building2 : Building2);

/** 深さに応じた navy 系グラデーション背景 (§2.2「全階層をnavy系グラデーション+アイコン形状で差別化」)。
 *  depth=0 (ルート) が最も濃く、深くなるほど段階的に薄くする。想定より深い階層でも配列末尾で頭打ちにする。 */
const DEPTH_BG_CLASSES = [
  'bg-misespark-navy',
  'bg-misespark-navy/85',
  'bg-misespark-navy/70',
  'bg-misespark-navy/55',
  'bg-misespark-navy/40',
  'bg-misespark-navy/25',
  'bg-misespark-navy/20',
  'bg-misespark-navy/15',
];

export const nodeDepthBgClass = (depth: number): string => DEPTH_BG_CLASSES[Math.min(Math.max(depth, 0), DEPTH_BG_CLASSES.length - 1)]!;

export type NodeTypeIconChipProps = {
  icon: string | null;
  depth: number;
  /** 停止中ノード用: グレースケール表示に切り替える (色だけに頼らないための3重表現は呼び出し元でBanアイコン+取消線+バッジを別途併記すること、WCAG 1.4.1) */
  dimmed?: boolean;
  className?: string;
};

export function NodeTypeIconChip({ icon, depth, dimmed = false, className = '' }: NodeTypeIconChipProps) {
  const Icon = resolveNodeTypeIcon(icon);
  return (
    <span
      className={`inline-flex items-center justify-center w-5 h-5 rounded shrink-0 ${dimmed ? 'bg-neutral-line' : nodeDepthBgClass(depth)} ${className}`}
    >
      <Icon size={12} className={dimmed ? 'text-neutral-sub' : 'text-white'} aria-hidden="true" />
    </span>
  );
}
